<script setup lang="ts">
import {
  CameraPlugin,
  ClipOverlayPlugin,
  ClipPlugin,
  ClipTask,
  ControlPlugin,
  createPotreeEngine,
  InputPlugin,
  MeshLoadCancelledError,
  MeshPlugin,
  PickingPlugin,
  RenderPlugin,
  ViewPlugin,
  type ClipService,
  type GltfResourcePreflightReport,
  type MeshHandle,
  type MeshFormat,
  type PotreeEngine,
} from '@sharefe/share3d-engine'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, triggerRef, useTemplateRef } from 'vue'
import { MESH_EXAMPLE_MODELS, type MeshExampleKey } from '../examples/meshModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { isGpuTimingRequested, toGraphicsConfig } from '../utils/graphicsUrlConfig'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import {
  consumeLocalFileSelection,
  registerLocalDirectoryEntry,
} from '../utils/localVirtualFiles'
import {
  createMeshExampleGltfLoaderConfig,
  createMeshExampleGltfRequestOptions,
  MESH_CHILD_CONCURRENCY_OPTIONS,
  MESH_GLTF_LOAD_MODE_OPTIONS,
  MESH_TEXTURE_CONCURRENCY_OPTIONS,
  MESH_TEXTURE_MAX_SIZE_OPTIONS,
  type MeshChildConcurrencyOption,
  type MeshGltfLoadModeOption,
  type MeshTextureConcurrencyOption,
  type MeshTextureMaxSizeOption,
} from '../utils/meshExampleLoaderOptions'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'

type ClipSelectionMode = 'polygon' | 'rectangle' | 'box3d'
type ClipApplyMode = 'inside' | 'outside'
const dracoPath = new URLSearchParams(location.search).get('dracoPath')

interface MeshExampleSource {
  readonly url: string
  readonly title: string
  readonly description: string
  readonly format?: MeshFormat
  readonly release?: () => void
}

const props = defineProps<{
  modelKey: MeshExampleKey
}>()

const containerRef = useTemplateRef('containerRef')
const graphicsCard = useExampleGraphicsCard({
  presetKey: 'coreMesh',
  extras: ['ClipPlugin', 'ClipOverlayPlugin'],
})
const glbInputRef = useTemplateRef('glbInputRef')
const meshResultInputRef = useTemplateRef('meshResultInputRef')
const model = computed(() => MESH_EXAMPLE_MODELS[props.modelKey])
const progress = ref(0)
const preflightReport = shallowRef<GltfResourcePreflightReport | null>(null)
const preflightPlan = computed(() => {
  const report = preflightReport.value
  return report?.selected ?? report?.candidates.at(-1)
})
const gpuBudgetSource = computed(() => {
  const source = preflightReport.value?.budget.gpuSource
  return source === 'host' ? '调用方快照' : source === 'explicit' ? '调用方预算' : '未配置'
})

function formatResourceBytes(bytes: number | null, unknownLabel = '未配置'): string {
  if (bytes === null || !Number.isFinite(bytes)) return unknownLabel
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GiB`
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MiB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KiB`
  return `${bytes} B`
}
const errorMessage = ref('')
const handle = shallowRef<MeshHandle | null>(null)
const loading = ref(true)
const engineReady = ref(false)
const currentTitle = ref(model.value.title)
const currentDescription = ref(model.value.description)
const currentSource = ref(model.value.url)
const gltfLoadMode = ref<MeshGltfLoadModeOption>('auto')
const textureMaxSize = ref<MeshTextureMaxSizeOption>('auto')
const textureConcurrency = ref<MeshTextureConcurrencyOption>(4)
const childConcurrency = ref<MeshChildConcurrencyOption>(2)
const forceDoubleSided = ref(true)
const clipSelectionMode = ref<ClipSelectionMode>('rectangle')
const clipApplyMode = ref<ClipApplyMode>('inside')
const clipActive = ref(false)
const clipCanConfirm = ref(false)
const clipCount = ref(0)
const isLiveBoxClip = computed(() => clipSelectionMode.value === 'box3d')
const gltfLoaderConfig = createMeshExampleGltfLoaderConfig(textureMaxSize.value)

let engine: PotreeEngine | null = null
let meshPlugin: MeshPlugin | null = null
let clipService: ClipService | null = null
let clipSubscription: ReturnType<ClipService['onChanged']> | null = null
let loadController: AbortController | null = null
let loadSequence = 0
let disposed = false
let currentLoadSource: MeshExampleSource | null = null

const statusText = computed(() => {
  if (errorMessage.value) return '加载失败'
  if (loading.value) {
    return `加载中 ${Math.round(progress.value * 100)}%${handle.value ? '（已显示部分模型）' : ''}`
  }
  if (handle.value) return '加载完成'
  return '等待加载'
})

function createMeshPlugins(meshPlugin: MeshPlugin) {
  return [
    new ViewPlugin(),
    new CameraPlugin(),
    new InputPlugin(),
    new PickingPlugin(),
    new RenderPlugin(),
    new ControlPlugin(),
    new ClipPlugin(),
    new ClipOverlayPlugin(),
    meshPlugin,
  ]
}

function fitModel(): void {
  engine?.fitAll()
}

function syncClipState(): void {
  if (!clipService) return
  clipSelectionMode.value = clipService.getSelectionMode()
  clipApplyMode.value = clipService.getClipApplyMode()
  clipCanConfirm.value = clipService.getDraftState().canConfirm
  clipCount.value = clipService.volumes.filter(
    (volume) => volume.clipTask !== ClipTask.HIGHLIGHT,
  ).length
  clipActive.value = engine?.activeTool === 'clip'
}

function activateClip(selectionMode: ClipSelectionMode): void {
  if (!engine || !clipService) return
  clipService.setSelectionMode(selectionMode)
  clipService.setClipApplyMode(clipApplyMode.value)
  engine.activateTool('clip')
  syncClipState()
}

function setClipApplyMode(applyMode: ClipApplyMode): void {
  if (!engine || !clipService || isLiveBoxClip.value) return
  clipService.setClipApplyMode(applyMode)
  engine.activateTool('clip')
  syncClipState()
}

function confirmClip(): void {
  clipService?.confirmDraft()
  syncClipState()
}

function clearClip(): void {
  if (!clipService) return
  engine?.deactivateTool()
  clipService.clearDraft()
  clipService.clearVolumes()
  syncClipState()
}

function disposeCurrentHandle(): void {
  const currentHandle = handle.value
  if (!currentHandle) return
  meshPlugin?.dispose(currentHandle.id)
  handle.value = null
}

async function loadMesh(source: MeshExampleSource): Promise<void> {
  const activeMeshPlugin = meshPlugin
  const activeEngine = engine
  if (!activeMeshPlugin || !activeEngine) {
    source.release?.()
    return
  }

  const sequence = ++loadSequence
  loadController?.abort('Mesh source replaced')
  if (currentLoadSource?.release !== source.release) currentLoadSource?.release?.()
  const controller = new AbortController()
  loadController = controller
  disposeCurrentHandle()
  progress.value = 0
  preflightReport.value = null
  errorMessage.value = ''
  loading.value = true
  currentTitle.value = source.title
  currentDescription.value = source.description
  currentSource.value = source.url
  currentLoadSource = { ...source }
  const showHandle = (loadedHandle: MeshHandle) => {
    const firstDisplay = handle.value === null
    activeMeshPlugin.mount(loadedHandle)
    handle.value = loadedHandle
    // 渐进更新保留同一句柄；显式通知 shallowRef 刷新 Primitive 与报告。
    triggerRef(handle)
    if (firstDisplay) activeEngine.fitAll()
  }

  try {
    const loadedHandle = await activeMeshPlugin.load({
      id: `mesh-example-${sequence}`,
      name: source.title,
      url: source.url,
      format: source.format,
      forceDoubleSided: forceDoubleSided.value,
      gltf: createMeshExampleGltfRequestOptions(gltfLoadMode.value, textureConcurrency.value),
      meshResult: { maxConcurrentLoads: childConcurrency.value },
      coordinate: {
        sourceUpAxis: 'y',
        targetUpAxis: 'z',
        origin: [0, 0, 0],
      },
      signal: controller.signal,
      onPreflight: (report) => {
        if (sequence === loadSequence) preflightReport.value = report
      },
      onProgress: (value: number) => {
        if (sequence === loadSequence) progress.value = value
      },
      onPartialLoad: (partialHandle) => {
        if (disposed || controller.signal.aborted || sequence !== loadSequence) return
        showHandle(partialHandle)
      },
    })

    if (disposed || controller.signal.aborted || sequence !== loadSequence) {
      activeMeshPlugin.dispose(loadedHandle.id)
      return
    }

    showHandle(loadedHandle)
  } catch (error) {
    // 引擎会释放失败请求的预览；同步清理借用句柄，避免显示失效统计和操作。
    if (sequence === loadSequence) handle.value = null
    if (
      disposed ||
      controller.signal.aborted ||
      sequence !== loadSequence ||
      error instanceof MeshLoadCancelledError
    ) {
      return
    }
    errorMessage.value = error instanceof Error ? error.message : String(error)
  } finally {
    if (sequence === loadSequence) {
      loading.value = false
      if (loadController === controller) loadController = null
    }
  }
}

function reloadCurrentMesh(): void {
  if (!engineReady.value || !currentLoadSource) return
  void loadMesh(currentLoadSource)
}

function handleTextureMaxSizeChange(): void {
  gltfLoaderConfig.setTextureMaxSize(textureMaxSize.value)
  reloadCurrentMesh()
}

function openGlbPicker(): void {
  glbInputRef.value?.click()
}

function openMeshResultPicker(): void {
  meshResultInputRef.value?.click()
}

async function handleGlbSelection(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const files = consumeLocalFileSelection(input)
  if (files.length === 0) return

  const { entryUrl, entryPath, release } = registerLocalDirectoryEntry(files, {
    entryExtensions: ['glb'],
  })
  if (!entryUrl || !entryPath) {
    errorMessage.value = '请选择有效的 .glb 文件'
    return
  }

  await loadMesh({
    url: entryUrl,
    title: entryPath.split('/').pop() ?? entryPath,
    description: '从本地文件导入的 GLB 静态 Mesh。',
    format: 'glb',
    release,
  })
}

async function handleMeshResultSelection(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const files = consumeLocalFileSelection(input)
  if (files.length === 0) return

  const { entryUrl, entryPath, totalFiles, release } = registerLocalDirectoryEntry(files, {
    entryFileNames: ['mesh_result.json'],
  })
  if (!entryUrl || !entryPath) {
    errorMessage.value = '所选目录中没有找到 mesh_result.json'
    return
  }

  await loadMesh({
    url: entryUrl,
    title: entryPath.split('/').pop() ?? entryPath,
    description: `从本地目录导入的分块 Mesh，共注册 ${totalFiles} 个文件。`,
    format: 'json',
    release,
  })
}

async function initialize(): Promise<void> {
  const container = containerRef.value
  if (!container) return

  const createdMeshPlugin = new MeshPlugin({
    ...(dracoPath ? { draco: { decoderPath: new URL(dracoPath, location.href).href } } : {}),
    ktx2: { transcoderPath: '/basis/', workerLimit: 2 },
    gltf: gltfLoaderConfig.loading,
    meshResult: { maxConcurrentLoads: 2 },
  })
  meshPlugin = createdMeshPlugin

  try {
    graphicsCard.markCreating()
    const createdEngine = await createPotreeEngine({
      container,
      plugins: createMeshPlugins(createdMeshPlugin),
      background: '#090f18',
      // `renderer` 只描述 WebGL2 渲染器；WebGPU 下由 graphics 后端自带配置，二者不能同时传。
      ...(graphicsCard.requestedBackend.value === 'webgpu'
        ? {}
        : { renderer: { antialias: true } }),
      graphics: toGraphicsConfig(graphicsCard.requestedBackend.value, {
        gpuProfiling: isGpuTimingRequested(),
      }),
      logging: {
        level: 'warn',
        namespaces: {
          'mesh:gltf': 'debug',
          'mesh:result-json': 'debug',
        },
      },
    })

    if (disposed) {
      createdEngine.dispose()
      return
    }

    engine = createdEngine
    // 仅性能采集显式请求时暴露只读 engine 探针，避免影响普通示例语义。
    if (isGpuTimingRequested()) Reflect.set(window, 'engine', createdEngine)
    graphicsCard.markReady(createdEngine.getGraphicsInfo().kind)
    engineReady.value = true
    const createdClipService = createdEngine.context.getService('ClipService')
    clipService = createdClipService
    clipSubscription = createdClipService.onChanged(() => syncClipState())
    createdEngine.events.on('tool.activated', syncClipState)
    createdEngine.events.on('tool.deactivated', syncClipState)
    syncClipState()

    await loadMesh({
      url: model.value.url,
      title: model.value.title,
      description: model.value.description,
    })
  } catch (error) {
    if (disposed || error instanceof MeshLoadCancelledError) return
    loading.value = false
    errorMessage.value = error instanceof Error ? error.message : String(error)
    // 只有引擎本身没建起来才算后端初始化失败，模型加载错误不应改写控制卡状态。
    if (!engine) {
      graphicsCard.markError(toGraphicsRuntimeError(error))
    }
  }
}

function disposeExample(): void {
  if (disposed) return
  disposed = true
  window.removeEventListener('pagehide', handlePageHide)
  loadSequence += 1
  loadController?.abort('Mesh example unmounted')
  loadController = null
  currentLoadSource?.release?.()
  currentLoadSource = null
  const activeEngine = engine
  const activeClipSubscription = clipSubscription
  if (activeEngine && Reflect.get(window, 'engine') === activeEngine) {
    Reflect.deleteProperty(window, 'engine')
  }
  engine = null
  meshPlugin = null
  clipService = null
  clipSubscription = null
  engineReady.value = false
  loading.value = false
  handle.value = null
  try {
    activeClipSubscription?.dispose()
  } catch {
    // 页面离开时仍须继续释放引擎与 WebGL context。
  }
  try {
    activeEngine?.events.off('tool.activated', syncClipState)
    activeEngine?.events.off('tool.deactivated', syncClipState)
  } catch {
    // EventBus 清理失败不得阻断 renderer 释放。
  }
  try {
    activeEngine?.dispose()
  } catch {
    // PotreeEngine 会继续执行全部释放阶段；页面离开时不再向 window 抛出清理错误。
  }
}

function handlePageHide(event: PageTransitionEvent): void {
  if (!event.persisted) disposeExample()
}

onMounted(() => {
  window.addEventListener('pagehide', handlePageHide)
  void initialize()
})

onBeforeUnmount(disposeExample)
</script>

<template>
  <main class="mesh-example">
    <div ref="containerRef" class="mesh-viewport"></div>

    <section class="mesh-panel" aria-live="polite">
      <GraphicsBackendControlCard :card="graphicsCard" />

      <p class="mesh-eyebrow">Static Mesh Example</p>
      <h1>{{ currentTitle }}</h1>
      <p class="mesh-description">{{ currentDescription }}</p>
      <p class="mesh-description">标准模式支持提前显示已就绪内容；完整加载后可点击“重新居中”查看全貌。</p>
      <p class="mesh-url" :title="currentSource">{{ currentSource }}</p>

      <div class="mesh-load-options">
        <label class="mesh-load-option">
          <span>加载模式</span>
          <select
            v-model="gltfLoadMode"
            :disabled="!engineReady"
            @change="reloadCurrentMesh"
          >
            <option
              v-for="option in MESH_GLTF_LOAD_MODE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="mesh-load-option">
          <span title="普通图片缩放；KTX2 选择不超过上限的已有 mip">纹理最长边上限</span>
          <select
            v-model="textureMaxSize"
            :disabled="!engineReady"
            @change="handleTextureMaxSizeChange"
          >
            <option
              v-for="option in MESH_TEXTURE_MAX_SIZE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="mesh-load-option">
          <span>纹理并发</span>
          <select
            v-model="textureConcurrency"
            :disabled="!engineReady"
            @change="reloadCurrentMesh"
          >
            <option
              v-for="value in MESH_TEXTURE_CONCURRENCY_OPTIONS"
              :key="value"
              :value="value"
            >
              {{ value }}
            </option>
          </select>
        </label>
        <label class="mesh-load-option">
          <span>分块并发</span>
          <select
            v-model="childConcurrency"
            :disabled="!engineReady"
            @change="reloadCurrentMesh"
          >
            <option v-for="value in MESH_CHILD_CONCURRENCY_OPTIONS" :key="value" :value="value">
              {{ value }}
            </option>
          </select>
        </label>
        <label class="mesh-load-option mesh-load-toggle">
          <input
            v-model="forceDoubleSided"
            type="checkbox"
            :disabled="!engineReady"
            @change="reloadCurrentMesh"
          />
          <span>强制双面材质</span>
        </label>
      </div>

      <div class="mesh-import">
        <button type="button" :disabled="!engineReady" @click="openGlbPicker">
          导入本地 GLB
        </button>
        <input
          ref="glbInputRef"
          class="mesh-file-input"
          type="file"
          accept=".glb,model/gltf-binary"
          @change="handleGlbSelection"
        />
        <button type="button" :disabled="!engineReady" @click="openMeshResultPicker">
          导入 mesh_result.json 目录
        </button>
        <input
          ref="meshResultInputRef"
          class="mesh-file-input"
          type="file"
          webkitdirectory
          multiple
          @change="handleMeshResultSelection"
        />
      </div>

      <div class="mesh-status-row">
        <span class="mesh-status-dot" :class="{ error: errorMessage }"></span>
        <span>{{ statusText }}</span>
      </div>

      <div v-if="loading && !handle && !errorMessage" class="mesh-progress">
        <span :style="{ width: `${Math.round(progress * 100)}%` }"></span>
      </div>

      <dl v-if="handle" class="mesh-stats">
        <div>
          <dt>格式</dt>
          <dd>{{ handle.format.toUpperCase() }}</dd>
        </div>
        <div v-if="handle.report.adoptedPath">
          <dt>加载路径</dt>
          <dd>{{ handle.report.adoptedPath }}</dd>
        </div>
        <div v-if="handle.report.textureSizing">
          <dt>普通图片档位 / 总纹理数</dt>
          <dd>
            {{ handle.report.textureSizing.selectedMaxSize }} px / {{
              handle.report.textureSizing.textureCount
            }} 张
          </dd>
        </div>
        <div>
          <dt>Primitive</dt>
          <dd>{{ handle.report.stats.primitiveCount }}</dd>
        </div>
        <div>
          <dt>三角形</dt>
          <dd>{{ handle.report.stats.triangleCount.toLocaleString() }}</dd>
        </div>
        <div>
          <dt>顶点</dt>
          <dd>{{ handle.report.stats.vertexCount.toLocaleString() }}</dd>
        </div>
      </dl>

      <div v-if="preflightPlan && preflightReport" class="mesh-preflight" aria-label="加载资源估算">
        <p>
          资源预估：内存峰值 {{ formatResourceBytes(preflightPlan.estimatedCpuPeakBytes, '未知') }}，
          显存需求 {{ formatResourceBytes(preflightPlan.estimatedGpuBytes, '未知') }}
        </p>
        <p>
          顶点属性 {{ formatResourceBytes(preflightReport.geometry.vertexAttributeBytes) }}，
          索引 {{ formatResourceBytes(preflightReport.geometry.indexBytes) }}，
          实例数据 {{ formatResourceBytes(preflightReport.geometry.instanceBytes) }}
        </p>
        <p v-if="preflightReport.budget.gpuBytes !== null">
          可用显存预算 {{ formatResourceBytes(preflightReport.budget.gpuBytes) }}（{{ gpuBudgetSource }}）
          <span v-if="preflightReport.budget.gpuUsedSource === 'included-in-available'">
            ，快照已扣除现有占用
          </span>
          <span v-else>
            ，已扣现有占用 {{ formatResourceBytes(preflightReport.budget.gpuUsedBytes) }}
          </span>
        </p>
        <p v-else>调用方未提供显存额度，当前仅估算资源需求。</p>
        <p>
          可分配纹理 {{ formatResourceBytes(preflightPlan.availableTextureBytes) }}，
          <span v-if="preflightReport.selected">
            加载后预算余量 {{ formatResourceBytes(preflightPlan.remainingGpuBytes, preflightReport.budget.gpuBytes === null ? '未配置' : '未知') }}
          </span>
          <span v-else>当前预算不足</span>
        </p>
        <span v-if="preflightReport.confidence === 'partial'">普通图片按完整档位方形粗略估算；KTX2 源尺寸及转码需求在预检时未知，未计入 Worker/WASM 与原图解码回退峰值。</span>
      </div>
      <p v-if="errorMessage" class="mesh-error">{{ errorMessage }}</p>

      <template v-if="handle">
        <div class="mesh-actions">
          <button type="button" @click="fitModel">重新居中</button>
          <button
            type="button"
            :class="{ active: clipActive && clipApplyMode === 'inside' }"
            :disabled="isLiveBoxClip"
            @click="setClipApplyMode('inside')"
          >
            内采
          </button>
          <button
            type="button"
            :class="{ active: clipActive && clipApplyMode === 'outside' }"
            :disabled="isLiveBoxClip"
            @click="setClipApplyMode('outside')"
          >
            外裁
          </button>
        </div>
        <div class="mesh-actions">
          <button
            type="button"
            :class="{ active: clipActive && clipSelectionMode === 'rectangle' }"
            @click="activateClip('rectangle')"
          >
            矩形裁切
          </button>
          <button
            type="button"
            :class="{ active: clipActive && clipSelectionMode === 'polygon' }"
            @click="activateClip('polygon')"
          >
            多边形裁切
          </button>
          <button
            type="button"
            :class="{ active: clipActive && clipSelectionMode === 'box3d' }"
            @click="activateClip('box3d')"
          >
            Box 裁切
          </button>
        </div>
        <div class="mesh-actions">
          <button
            v-if="!isLiveBoxClip"
            type="button"
            :disabled="!clipCanConfirm"
            @click="confirmClip"
          >
            确认 2D 选区
          </button>
          <button
            type="button"
            :disabled="clipCount === 0 && !clipCanConfirm"
            @click="clearClip"
          >
            清空裁切（{{ clipCount }}）
          </button>
        </div>
      </template>
      <p class="mesh-hint">
        修改加载选项会重新加载当前模型。可导入本地 GLB 替换当前模型；2D
        选区绘制后点击确认，Box 裁切实时生效。
      </p>
    </section>
  </main>
</template>

<style scoped>
.mesh-example {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  color: #e8f0fa;
  background: #090f18;
}

.mesh-viewport {
  width: 100%;
  height: 100%;
}

.mesh-panel {
  position: absolute;
  top: 18px;
  left: 18px;
  width: min(330px, calc(100vw - 36px));
  max-height: calc(100% - 36px);
  overflow-y: auto;
  padding: 18px;
  border: 1px solid rgba(148, 197, 255, 0.2);
  border-radius: 16px;
  background: rgba(8, 14, 23, 0.82);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.32);
  box-sizing: border-box;
  backdrop-filter: blur(14px);
}

.mesh-eyebrow {
  margin: 0 0 7px;
  color: #8dc8ff;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 24px;
}

.mesh-description {
  margin: 9px 0 0;
  color: rgba(232, 240, 250, 0.74);
  font-size: 13px;
  line-height: 1.55;
}

.mesh-url {
  margin: 12px 0 0;
  overflow: hidden;
  color: rgba(166, 199, 232, 0.66);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mesh-load-options {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  margin-top: 14px;
}

.mesh-load-option {
  display: flex;
  flex-direction: column;
  gap: 7px;
  color: rgba(203, 218, 235, 0.68);
  font-size: 11px;
}

.mesh-load-option select {
  min-width: 0;
  padding: 7px 9px;
  border: 1px solid rgba(125, 190, 255, 0.24);
  border-radius: 8px;
  outline: none;
  background: rgba(15, 31, 48, 0.92);
  color: #dbeafe;
}

.mesh-load-option select:focus-visible {
  border-color: rgba(56, 189, 248, 0.8);
}

.mesh-load-toggle {
  flex-direction: row;
  align-items: center;
  align-self: end;
  min-height: 32px;
}

.mesh-load-toggle input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #38bdf8;
}

.mesh-load-option :disabled {
  cursor: not-allowed;
  opacity: 0.46;
}

.mesh-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  font-size: 12px;
}

.mesh-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 12px rgba(74, 222, 128, 0.7);
}

.mesh-status-dot.error {
  background: #fb7185;
  box-shadow: 0 0 12px rgba(251, 113, 133, 0.7);
}

.mesh-progress {
  height: 3px;
  margin-top: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
}

.mesh-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #38bdf8, #818cf8);
  transition: width 120ms ease;
}

.mesh-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 0;
}

.mesh-stats div {
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
}

.mesh-stats dt {
  color: rgba(203, 218, 235, 0.56);
  font-size: 10px;
}

.mesh-stats dd {
  margin: 4px 0 0;
  font-size: 13px;
}

.mesh-error {
  margin: 12px 0 0;
  color: #fda4af;
  font-size: 12px;
  line-height: 1.5;
}

.mesh-preflight {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 10px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
}

.mesh-preflight p {
  margin: 0;
}

.mesh-preflight p + p {
  margin-top: 6px;
}

.mesh-preflight > span {
  display: block;
  margin-top: 6px;
  color: #fbbf24;
}

.mesh-import {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mesh-file-input {
  display: none;
}

button {
  margin-top: 14px;
  padding: 8px 12px;
  border: 1px solid rgba(125, 190, 255, 0.3);
  border-radius: 9px;
  background: rgba(59, 130, 246, 0.16);
  color: #dbeafe;
  cursor: pointer;
}

button:hover {
  background: rgba(59, 130, 246, 0.26);
}

button.active {
  border-color: rgba(56, 189, 248, 0.8);
  background: rgba(14, 165, 233, 0.3);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.46;
}

.mesh-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.mesh-actions + .mesh-actions {
  margin-top: 7px;
}

.mesh-actions:first-of-type {
  margin-top: 14px;
}

.mesh-actions button {
  margin-top: 0;
}

.mesh-hint {
  margin: 12px 0 0;
  color: rgba(203, 218, 235, 0.5);
  font-size: 11px;
}

@media (max-width: 600px) {
  .mesh-panel {
    top: 12px;
    left: 12px;
    width: calc(100vw - 24px);
  }
}
</style>
